import { useCallback, useState } from 'react'
import TurnstileWidget from '../turnstile-widget/TurnstileWidget'
import type { GuestFormProps, GuestFormValues } from './types'
import {
  AddAttendeeButton,
  AttendeeRow,
  ButtonRow,
  ErrorBanner,
  FieldGroup,
  FieldInput,
  FieldLabel,
  FieldTextarea,
  FormColumn,
  PrimaryButton,
  RecurringCard,
  RecurringCheckbox,
  RecurringCopy,
  RecurringDetail,
  RecurringTitle,
  RemoveAttendeeButton,
  SecondaryButton,
} from './styled'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u

const FREQUENCY_LABELS: Record<'weekly' | 'biweekly' | 'monthly', string> = {
  weekly: 'every week',
  biweekly: 'every 2 weeks',
  monthly: 'every month',
}

const GuestForm = ({
  initialValues,
  submitLabel,
  isSubmitting,
  errorMessage,
  allowAdditionalAttendees,
  recurringHint,
  turnstileResetSignal = 0,
  onCancel,
  onSubmit,
}: GuestFormProps): JSX.Element => {
  const [name, setName] = useState<string>(initialValues?.name ?? '')
  const [email, setEmail] = useState<string>(initialValues?.email ?? '')
  const [phone, setPhone] = useState<string>(initialValues?.phone ?? '')
  const [notes, setNotes] = useState<string>(initialValues?.notes ?? '')
  const [additionalAttendees, setAdditionalAttendees] = useState<string[]>(
    initialValues?.additionalAttendees ?? [],
  )
  const [isRecurring, setIsRecurring] = useState<boolean>(
    initialValues?.recurring ?? false,
  )
  const [turnstileToken, setTurnstileToken] = useState<string>('')
  const [validationError, setValidationError] = useState<string>('')

  const handleAddAttendee = useCallback((): void => {
    setAdditionalAttendees(previous => [...previous, ''])
  }, [])

  const handleRemoveAttendee = useCallback((indexToRemove: number): void => {
    setAdditionalAttendees(previous =>
      previous.filter((_, attendeeIndex) => attendeeIndex !== indexToRemove),
    )
  }, [])

  const handleAttendeeChange = useCallback(
    (indexToUpdate: number, nextValue: string): void => {
      setAdditionalAttendees(previous =>
        previous.map((existingValue, attendeeIndex) =>
          attendeeIndex === indexToUpdate ? nextValue : existingValue,
        ),
      )
    },
    [],
  )

  const handleSubmit = useCallback(
    (formEvent: React.FormEvent<HTMLFormElement>): void => {
      formEvent.preventDefault()
      const trimmedName = name.trim()
      const trimmedEmail = email.trim()

      if (trimmedName.length === 0) {
        setValidationError('Please enter your name.')
        return
      }
      if (!EMAIL_PATTERN.test(trimmedEmail)) {
        setValidationError('Please enter a valid email address.')
        return
      }

      if (turnstileToken.length === 0) {
        setValidationError(
          'Please complete the bot verification challenge before booking.',
        )
        return
      }

      const cleanedAttendees: string[] = []
      for (const attendeeEmail of additionalAttendees) {
        const trimmed = attendeeEmail.trim()
        if (trimmed.length === 0) {
          continue
        }
        if (!EMAIL_PATTERN.test(trimmed)) {
          setValidationError(`"${trimmed}" doesn't look like a valid email.`)
          return
        }
        cleanedAttendees.push(trimmed)
      }

      setValidationError('')
      const values: GuestFormValues = {
        name: trimmedName,
        email: trimmedEmail,
        phone: phone.trim(),
        notes: notes.trim(),
        additionalAttendees: cleanedAttendees,
        recurring: recurringHint !== null ? isRecurring : false,
        turnstileToken,
      }
      onSubmit(values)
    },
    [
      name,
      email,
      phone,
      notes,
      additionalAttendees,
      isRecurring,
      recurringHint,
      turnstileToken,
      onSubmit,
    ],
  )

  const visibleError = validationError || errorMessage

  return (
    <FormColumn onSubmit={handleSubmit}>
      <FieldGroup>
        <FieldLabel htmlFor="guest-name">Name</FieldLabel>
        <FieldInput
          id="guest-name"
          type="text"
          required
          value={name}
          placeholder="Jamie Smith"
          onChange={changeEvent => setName(changeEvent.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel htmlFor="guest-email">Email</FieldLabel>
        <FieldInput
          id="guest-email"
          type="email"
          required
          value={email}
          placeholder="you@example.com"
          onChange={changeEvent => setEmail(changeEvent.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel htmlFor="guest-phone">Phone (optional)</FieldLabel>
        <FieldInput
          id="guest-phone"
          type="tel"
          value={phone}
          placeholder="+1 (555) 555-1234"
          onChange={changeEvent => setPhone(changeEvent.target.value)}
        />
      </FieldGroup>

      {allowAdditionalAttendees ? (
        <FieldGroup>
          <FieldLabel>Additional attendees (optional)</FieldLabel>
          {additionalAttendees.map((attendeeEmail, attendeeIndex) => (
            <AttendeeRow key={`attendee-${attendeeIndex}`}>
              <FieldInput
                type="email"
                value={attendeeEmail}
                placeholder="colleague@example.com"
                onChange={changeEvent =>
                  handleAttendeeChange(
                    attendeeIndex,
                    changeEvent.target.value,
                  )
                }
              />
              <RemoveAttendeeButton
                type="button"
                onClick={() => handleRemoveAttendee(attendeeIndex)}
                aria-label="Remove this attendee"
              >
                ✕
              </RemoveAttendeeButton>
            </AttendeeRow>
          ))}
          <AddAttendeeButton
            type="button"
            onClick={handleAddAttendee}
            disabled={isSubmitting}
          >
            + Add a guest
          </AddAttendeeButton>
        </FieldGroup>
      ) : null}

      <FieldGroup>
        <FieldLabel htmlFor="guest-notes">
          Anything you&apos;d like the host to know? (optional)
        </FieldLabel>
        <FieldTextarea
          id="guest-notes"
          value={notes}
          placeholder="Mention a topic, a question, or a property of interest."
          onChange={changeEvent => setNotes(changeEvent.target.value)}
        />
      </FieldGroup>

      {recurringHint !== null ? (
        <RecurringCard>
          <RecurringCheckbox
            type="checkbox"
            checked={isRecurring}
            onChange={changeEvent => setIsRecurring(changeEvent.target.checked)}
          />
          <RecurringCopy>
            <RecurringTitle>Make this a recurring booking</RecurringTitle>
            <RecurringDetail>
              We&apos;ll book {recurringHint.count} occurrences{' '}
              {FREQUENCY_LABELS[recurringHint.frequency]}, starting at the time
              you picked. Each one is confirmed or rejected as a single unit.
            </RecurringDetail>
          </RecurringCopy>
        </RecurringCard>
      ) : null}

      {visibleError.length > 0 ? <ErrorBanner>{visibleError}</ErrorBanner> : null}

      <TurnstileWidget
        onTokenChange={setTurnstileToken}
        resetSignal={turnstileResetSignal}
      />

      <ButtonRow>
        <SecondaryButton
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Back
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : submitLabel}
        </PrimaryButton>
      </ButtonRow>
    </FormColumn>
  )
}

export default GuestForm
